import { Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

import { formatMoney } from './final-settlement-parts';
import type { PaymentTranche } from './final-settlement-invoice-schema';

// Mirrors Launchpad's trigger menu. Values are the payment_trigger strings the
// backend stores verbatim (and later resolves to a shipment-milestone date).
const TRIGGER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ATA at PoD', label: 'ATA at PoD' },
  { value: 'ATA at Final destination', label: 'ATA at Final Destination' },
  { value: 'ATA at Customer Site', label: 'ATA @ Customer Site' },
  { value: 'ETA at PoD', label: 'ETA at PoD' },
  { value: 'ETA at Final destination', label: 'ETA at Final Destination' },
  { value: 'ETD', label: 'ETD' },
  { value: 'BL issuance', label: 'BL Issue Date' },
  { value: 'BL release', label: 'BL Release' },
  { value: 'BL confirmed', label: 'BL Confirmed' },
  { value: 'Loading', label: 'Loading Date' },
  { value: 'Invoice', label: 'Invoice Issue Date' },
  { value: 'Booking', label: 'Booking Date' },
  { value: 'Customs Clearance', label: 'Customs Clearance' },
  { value: 'Fixed Date', label: 'Fixed Date' },
];

const SUM_TOLERANCE = 0.01;

export interface PaymentScheduleEditorProps {
  readonly totalAmount: number;
  readonly currency: string | null;
  readonly contractTermsLabel: string | null;
  readonly triggerLine: string | null;
  readonly tranches: PaymentTranche[];
  readonly onTranchesChange: (tranches: PaymentTranche[]) => void;
  readonly disabled?: boolean;
}

function paymentScheduleSum(tranches: readonly PaymentTranche[]): number {
  return tranches.reduce((sum, t) => sum + (t.percentage || 0), 0);
}

export function PaymentScheduleEditor(props: PaymentScheduleEditorProps) {
  const total = paymentScheduleSum(props.tranches);
  const valid = Math.abs(total - 100) < SUM_TOLERANCE;
  const disabled = props.disabled ?? false;
  const update = (index: number, patch: Partial<PaymentTranche>) => {
    props.onTranchesChange(props.tranches.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };
  return (
    <div className="flex flex-col gap-3 border-t pt-3" data-testid="payment-schedule-editor">
      <ScheduleHeader total={total} valid={valid} />
      {props.contractTermsLabel !== null && (
        <ContractTermsBox label={props.contractTermsLabel} triggerLine={props.triggerLine} />
      )}
      {props.tranches.map((tranche, index) => (
        <TrancheRow
          key={index}
          index={index}
          tranche={tranche}
          amount={(props.totalAmount * (tranche.percentage || 0)) / 100}
          currency={props.currency}
          disabled={disabled}
          removable={props.tranches.length > 1}
          onChange={(patch) => {
            update(index, patch);
          }}
          onRemove={() => {
            props.onTranchesChange(props.tranches.filter((_, i) => i !== index));
          }}
        />
      ))}
      <ScheduleFooter
        total={total}
        valid={valid}
        disabled={disabled}
        onAdd={() => {
          props.onTranchesChange([
            ...props.tranches,
            { percentage: Math.max(0, 100 - total), trigger: 'ATA at PoD' },
          ]);
        }}
      />
    </div>
  );
}

function ScheduleHeader(props: { readonly total: number; readonly valid: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-sm font-semibold">Payment Schedule</Label>
      <Badge
        variant={props.valid ? 'success' : 'destructive'}
        data-testid="payment-schedule-allocation"
      >
        {props.total.toFixed(0)}% / 100%
      </Badge>
    </div>
  );
}

function ScheduleFooter(props: {
  readonly total: number;
  readonly valid: boolean;
  readonly disabled: boolean;
  readonly onAdd: () => void;
}) {
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        disabled={props.disabled}
        onClick={props.onAdd}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add Tranche
      </Button>
      {!props.valid && props.total > 0 && (
        <p className="text-destructive text-xs" data-testid="payment-schedule-error">
          Percentages must sum to 100% (currently {props.total.toFixed(1)}%)
        </p>
      )}
    </>
  );
}

function ContractTermsBox(props: { readonly label: string; readonly triggerLine: string | null }) {
  return (
    <div className="bg-muted/50 border-border/50 flex flex-col gap-0.5 rounded-md border p-2.5">
      <p className="text-muted-foreground text-xs font-medium">Contract Payment Terms</p>
      <p className="text-foreground text-xs">{props.label}</p>
      {props.triggerLine !== null && (
        <p className="text-muted-foreground text-xs">{props.triggerLine}</p>
      )}
    </div>
  );
}

interface TrancheRowProps {
  readonly index: number;
  readonly tranche: PaymentTranche;
  readonly amount: number;
  readonly currency: string | null;
  readonly disabled: boolean;
  readonly removable: boolean;
  readonly onChange: (patch: Partial<PaymentTranche>) => void;
  readonly onRemove: () => void;
}

function TrancheRow(props: TrancheRowProps) {
  return (
    <div className="bg-muted/30 flex items-end gap-2 rounded-lg border p-3">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground font-medium">Tranche {props.index + 1}</span>
          <span className="text-muted-foreground">
            = {formatMoney(props.amount, props.currency)}
          </span>
        </div>
        <TrancheFields
          tranche={props.tranche}
          disabled={props.disabled}
          onChange={props.onChange}
        />
      </div>
      {props.removable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive h-8 w-8 shrink-0"
          disabled={props.disabled}
          aria-label={`Remove tranche ${String(props.index + 1)}`}
          onClick={props.onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function TrancheFields(props: {
  readonly tranche: PaymentTranche;
  readonly disabled: boolean;
  readonly onChange: (patch: Partial<PaymentTranche>) => void;
}) {
  const { tranche } = props;
  return (
    <div className="grid grid-cols-3 gap-2">
      <div>
        <Label className="text-xs">% Share</Label>
        <Input
          type="number"
          min={0}
          max={100}
          step={0.01}
          className="h-8 text-sm"
          disabled={props.disabled}
          value={tranche.percentage === 0 ? '' : tranche.percentage}
          onChange={(e) => {
            props.onChange({ percentage: parseFloat(e.target.value) || 0 });
          }}
        />
      </div>
      <div>
        <Label className="text-xs">Trigger</Label>
        <Select
          className="h-8 text-sm"
          disabled={props.disabled}
          value={tranche.trigger}
          onChange={(e) => {
            props.onChange({ trigger: e.target.value });
          }}
        >
          {TRIGGER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </div>
      <TrancheDateCell tranche={tranche} disabled={props.disabled} onChange={props.onChange} />
    </div>
  );
}

function TrancheDateCell(props: {
  readonly tranche: PaymentTranche;
  readonly disabled: boolean;
  readonly onChange: (patch: Partial<PaymentTranche>) => void;
}) {
  if (props.tranche.trigger !== 'Fixed Date') {
    return (
      <div className="flex items-end">
        <p className="text-muted-foreground pb-2 text-xs">Due date auto-computed</p>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs">Due Date</Label>
      <Input
        type="date"
        className="h-8 text-sm"
        disabled={props.disabled}
        value={props.tranche.fixedDate ?? ''}
        onChange={(e) => {
          props.onChange({ fixedDate: e.target.value });
        }}
      />
    </div>
  );
}
